import React, { useMemo, useState } from 'react';
import classNames from 'clsx';
import { FormControl, TextField } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import Phone from '@material-ui/icons/Phone';
import Lock from 'mdi-material-ui/Lock';
import LockOutline from 'mdi-material-ui/LockOutline';

import { ControlContainer } from 'app/components/ControlContainer';
import { UtilService } from 'app/services/util.service';
import { PhoneNumberControlProps } from './PhoneNumberControl.interfaces';
import { FormsEnum, LabelEnum, MubiLeasingEnum } from 'app/shared/enums';
import { useStyles } from './PhoneNumberControl.styles';
import { usePhoneNumberControl } from 'app/shared/hooks/controls/usePhoneNumberControl';
import { TipMessage } from 'app/components/TipMessage';

export default function PhoneNumberControl(props: PhoneNumberControlProps) {
  const classes = useStyles();

  const {
    meta,
    tooltip,
    input,
    disabled,
    label,
    subLabel,
    helperText,
    width,
    margin,
    bulbInfoMessage,
    larger,
    placeholder,
    animateLabel,
    className,
    defaultAlertStyle,
    customIcon,
    customLabelSpacing,
    formName,
  } = props;

  const {
    error,
    handleBlur,
    handleChange,
    onFocus,
    handleClick,
    prefix,
    isMubiLeasingConfig,
  } = usePhoneNumberControl(props);

  const [isFocused, setIsFocused] = useState(false);
  const isLabelShrunk = isFocused || UtilService.hasValue(input.value);

  const tipMessage = useMemo(() => {
    return bulbInfoMessage ? (
      isMubiLeasingConfig ? (
        <TipMessage
          strongContent={MubiLeasingEnum.WeWillContactYouToPresentOffers}
          content={bulbInfoMessage.content}
          icon={<LockOutline className={classes.lockOutlinedIcon} />}
          className={classes.tipMessageGreen}
        />
      ) : (
        <TipMessage
          strongContent={LabelEnum.EmailSmsSent}
          content={bulbInfoMessage.content}
          icon={<Lock className={classes.lockIcon} />}
        />
      )
    ) : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulbInfoMessage, isMubiLeasingConfig, classes.tipMessageGreen]);

  const labelDense = !(
    formName === FormsEnum.TerminationGeneratorForm ||
    isMubiLeasingConfig ||
    customLabelSpacing
  );

  return (
    <FormControl
      fullWidth
      className={animateLabel ? classes.withAdornment : undefined}
    >
      <ControlContainer
        inputId={input.name}
        meta={meta}
        tooltip={tooltip}
        label={animateLabel ? undefined : label}
        subLabel={subLabel}
        helperText={helperText}
        floating
        floated={
          animateLabel ? isFocused || UtilService.hasValue(input.value) : true
        }
        tipMessage={tipMessage}
        classes={{
          label: labelDense ? classes.labelDense : classes.label,
        }}
        disabled={disabled}
        width={width}
        className={className}
        defaultAlertStyle={defaultAlertStyle}
      >
        {!animateLabel &&
          (customIcon ? (
            customIcon
          ) : (
            <Phone
              aria-hidden="true"
              className={classNames(
                {
                  [classes.activeIcon]:
                    props.isQuestionValid && UtilService.hasValue(input.value),
                },
                classes.icon,
                larger && classes.iconLarger
              )}
            />
          ))}
        <TextField
          id={input.name}
          label={animateLabel ? label : undefined}
          InputLabelProps={{
            shrink: isLabelShrunk,
          }}
          InputProps={
            animateLabel
              ? {
                  name: input.name,
                  disabled,
                  startAdornment: (
                    <InputAdornment position={'start'} disablePointerEvents>
                      <Phone className={classes.adornmentIcon} />
                      {isLabelShrunk && (
                        <span className={classes.adornmentPrefix}>
                          {prefix}
                        </span>
                      )}
                    </InputAdornment>
                  ),
                }
              : {
                  name: input.name,
                  disabled: disabled,
                  startAdornment: (
                    <InputAdornment
                      position={'start'}
                      className={
                        larger
                          ? classes.inputAdornmentLarger
                          : classes.inputAdornment
                      }
                    >
                      <span>{prefix}</span>
                    </InputAdornment>
                  ),
                  className: classes.textField,
                  classes: {
                    input: classNames(classes.input, {
                      [classes.inputLarger]: larger,
                    }),
                  },
                }
          }
          error={error}
          variant={'outlined'}
          onChange={handleChange}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus(e as React.FocusEvent<HTMLInputElement>);
          }}
          onBlur={() => {
            setIsFocused(false);
            handleBlur();
          }}
          onClick={handleClick}
          margin={margin || 'dense'}
          placeholder={
            animateLabel ? undefined : placeholder || LabelEnum.PhonePlaceholder
          }
          type="tel"
          value={input.value}
          fullWidth
        />
      </ControlContainer>
    </FormControl>
  );
}
